import { gsap } from 'gsap';

/**
 * Pointer-following tilt/pan/zoom for a card photo, optionally with a label
 * that trails the pointer. Fine-pointer devices only; skipped for
 * prefers-reduced-motion.
 *
 * Kept deliberately cheap: one reusable tween per property (no tween
 * allocation per mousemove), pointer samples coalesced to one update per
 * frame, and the photo/label only promoted to a compositor layer while the
 * area carries `hoverClass` (the stylesheet scopes `will-change` to it).
 *
 * @param {object} options
 * @param {HTMLElement} options.area - Element that receives the hover and defines the pointer rect.
 * @param {HTMLElement} options.photo - Element that tilts, pans and scales.
 * @param {HTMLElement} [options.cursor] - Optional label that trails the pointer.
 * @param {number} [options.scale=1.16]
 * @param {number} [options.pan=56] - Max pan in px.
 * @param {number} [options.tilt=9] - Max tilt in degrees.
 * @param {string} [options.hoverClass='is-hovering']
 * @returns {() => void} Tear-down function.
 *
 * @example
 *   enableTiltHover({ area: card, photo: card.querySelector('.image') });
 */
export function enableTiltHover({ area, photo, cursor = null, scale = 1.16, pan = 56, tilt = 9, hoverClass = 'is-hovering' }) {
  if (!area || !photo) return () => {};
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return () => {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};

  const ease = 'power3.out';
  gsap.set(photo, { transformPerspective: 900 });
  const photoTo = {
    x: gsap.quickTo(photo, 'x', { duration: 0.7, ease }),
    y: gsap.quickTo(photo, 'y', { duration: 0.7, ease }),
    rotationX: gsap.quickTo(photo, 'rotationX', { duration: 0.7, ease }),
    rotationY: gsap.quickTo(photo, 'rotationY', { duration: 0.7, ease }),
  };
  const scaleTo = (value, duration) => gsap.to(photo, { scale: value, duration, ease, overwrite: 'auto' });

  let cursorTo = null;
  if (cursor) {
    gsap.set(cursor, { xPercent: -50, yPercent: -50, x: 0, y: 0, opacity: 0, scale: 0.85 });
    cursorTo = {
      x: gsap.quickTo(cursor, 'x', { duration: 0.45, ease }),
      y: gsap.quickTo(cursor, 'y', { duration: 0.45, ease }),
    };
  }

  // Last known pointer position in viewport coordinates. The area's rect is
  // re-read on every frame we apply, not cached on enter: scrolling with the
  // pointer resting on the card moves the card under it, and a stale rect
  // would leave the label and tilt pointing at the wrong spot.
  let pointer = null;
  let raf = 0;
  let hovering = false;

  const applyMove = () => {
    raf = 0;
    if (!pointer || !hovering) return;
    const rect = area.getBoundingClientRect();
    const x = pointer.x - rect.left;
    const y = pointer.y - rect.top;
    cursorTo?.x(x);
    cursorTo?.y(y);

    const nx = rect.width ? (x / rect.width) * 2 - 1 : 0;
    const ny = rect.height ? (y / rect.height) * 2 - 1 : 0;
    photoTo.x(nx * pan);
    photoTo.y(ny * pan);
    photoTo.rotationX(-ny * tilt);
    photoTo.rotationY(nx * tilt);
  };

  const schedule = () => {
    if (!raf) raf = requestAnimationFrame(applyMove);
  };

  const onMove = (e) => {
    pointer = { x: e.clientX, y: e.clientY };
    schedule();
  };

  // Scrolling moves the card under a resting pointer; follow it.
  const onScroll = () => {
    if (hovering) schedule();
  };

  const onEnter = (e) => {
    hovering = true;
    area.classList.add(hoverClass);
    if (cursor) {
      const rect = area.getBoundingClientRect();
      gsap.set(cursor, { x: e.clientX - rect.left, y: e.clientY - rect.top });
      gsap.to(cursor, { opacity: 1, scale: 1, duration: 0.35, ease: 'power2.out' });
    }
    scaleTo(scale, 0.7);
    window.addEventListener('scroll', onScroll, { passive: true });
    onMove(e);
  };

  const onLeave = () => {
    hovering = false;
    window.removeEventListener('scroll', onScroll);
    area.classList.remove(hoverClass);
    pointer = null;
    if (cursor) gsap.to(cursor, { opacity: 0, scale: 0.85, duration: 0.3, ease: 'power2.in' });
    photoTo.x(0);
    photoTo.y(0);
    photoTo.rotationX(0);
    photoTo.rotationY(0);
    scaleTo(1, 0.6);
  };

  area.addEventListener('mouseenter', onEnter);
  area.addEventListener('mousemove', onMove);
  area.addEventListener('mouseleave', onLeave);

  return () => {
    area.removeEventListener('mouseenter', onEnter);
    area.removeEventListener('mousemove', onMove);
    area.removeEventListener('mouseleave', onLeave);
    window.removeEventListener('scroll', onScroll);
    if (raf) cancelAnimationFrame(raf);
  };
}
